import { ChangeEvent, FC } from 'react';

import {
    ChevronDownIcon,
    Controller,
    RadioButtonGroup,
    StirlingIcon,
    TextField,
} from '@selina-finance/ui';
import { Formik } from 'formik';
import { pick } from 'lodash';

import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
    radioButtonOptions,
} from '@Shared/helpers/input';
import { employed } from '@src/app/formModels/eligibility/incomings.employed';
import CallOut from '@src/ui/components/CallOut/Callout';
import IncomingOtherIncomesSubView from '@src/ui/views/eligibility/IncomingOtherIncomesSubView';

import ErrorContainer from '../../components/ErrorContainer/ErrorContainer';
import SelectBox from '../../components/SelectBox/SelectBox';

import ExpectedDecrease from './IncomingExpectedDecreaseField';

export interface IncomingEmployedViewProps {
    className?: string;
    secondary?: boolean;
    incomeData?: any;
    isPrimaryApplicant: boolean;
    setRef?: Function;
}

const defaultValues = getDefaultValues(employed);
const validationSchema = getValidationSchema(employed);

const IncomingEmployedView: FC<IncomingEmployedViewProps> = (props) => {
    return (
        <Formik
            initialValues={(() => {
                const base = {
                    ...defaultValues,
                    ...pick(props.incomeData, Object.keys(employed)),
                };
                const emp = base.employment;
                if (emp?.contractStartDate && !emp?.startMonth) {
                    const parts = emp.contractStartDate.split('/');
                    base.employment = {
                        ...emp,
                        startMonth: parts[1] || '',
                        startYear: parts[2] || '',
                    };
                }
                return base;
            })()}
            validationSchema={validationSchema}
            onSubmit={(values) => values}
            innerRef={(ref) => {
                if (props.setRef) props.setRef(ref);
            }}
        >
            {(formikProps) => {
                const { getPropsWithError, getError } = getFormHelpers(
                    employed,
                    formikProps,
                );

                return (
                    <div className='grid grid-cols-1 gap-10 m-0'>
                        <TextField
                            name='employment.registeredCompanyName'
                            label={`What ${
                                props.isPrimaryApplicant
                                    ? 'is your employer’s registered company name?'
                                    : 'company do they work for?'
                            }`}
                            maxLength={255}
                            value={
                                formikProps.values.employment.registeredCompanyName?.toString() ||
                                ''
                            }
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                formikProps.handleChange(e)
                            }
                            onBlur={formikProps.handleBlur}
                            error={getError('employment.registeredCompanyName')}
                        />
                        <Controller
                            label={`What was ${
                                props.isPrimaryApplicant ? 'your' : 'their'
                            } start date?`}
                        >
                            <div className='grid grid-cols-1 gap-2 grid-cols-2'>
                                <SelectBox
                                    items={employed.employment.month.items}
                                    suffix={<ChevronDownIcon />}
                                    label='Month'
                                    labelSize='small'
                                    name='startMonth'
                                    placeholder='Select'
                                    validState={
                                        !!formikProps.values.employment
                                            .startMonth
                                    }
                                    value={
                                        formikProps.values.employment.startMonth?.toString() ||
                                        ''
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const month = e.currentTarget.value;
                                        formikProps.setTouched({
                                            ...formikProps.touched,
                                            'employment.startMonth': true,
                                        });
                                        formikProps.setFieldValue(
                                            'employment.startMonth',
                                            month,
                                        );
                                        if (
                                            formikProps.values.employment
                                                .startYear
                                        ) {
                                            formikProps.setFieldValue(
                                                'employment.contractStartDate',
                                                `01/${month}/${formikProps.values.employment.startYear}`,
                                            );
                                        }
                                    }}
                                    error={getError('employment.startMonth')}
                                    onBlur={() => {
                                        return formikProps.setTouched({
                                            ...formikProps.touched,
                                            'employment.startMonth': true,
                                        });
                                    }}
                                />
                                <SelectBox
                                    items={employed.employment.year.items}
                                    suffix={<ChevronDownIcon />}
                                    label='Year'
                                    labelSize='small'
                                    name='startYear'
                                    placeholder='Select'
                                    validState={
                                        !!formikProps.values.employment
                                            .startYear
                                    }
                                    value={
                                        formikProps.values.employment.startYear?.toString() ||
                                        ''
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const year = e.currentTarget.value;
                                        formikProps.setTouched({
                                            ...formikProps.touched,
                                            'employment.startYear': true,
                                        });
                                        formikProps.setFieldValue(
                                            'employment.startYear',
                                            year,
                                        );
                                        if (
                                            formikProps.values.employment
                                                .startMonth
                                        ) {
                                            formikProps.setFieldValue(
                                                'employment.contractStartDate',
                                                `01/${formikProps.values.employment.startMonth}/${year}`,
                                            );
                                        }
                                    }}
                                    error={getError('employment.startYear')}
                                    onBlur={() => {
                                        return formikProps.setTouched({
                                            ...formikProps.touched,
                                            'employment.startYear': true,
                                        });
                                    }}
                                />
                            </div>
                        </Controller>
                        <Controller
                            label={`Do ${
                                props.isPrimaryApplicant
                                    ? 'you own more than 25% of the company in shares?'
                                    : 'they own more than 25% of the company?'
                            }`}
                        >
                            <RadioButtonGroup
                                name='employment.ownSharesInThisCompany'
                                id='employment.ownSharesInThisCompany'
                                value={
                                    formikProps.values.employment.ownSharesInThisCompany?.toString() ||
                                    ''
                                }
                                error={{
                                    hasError: false,
                                    errorMessage: getError(
                                        'employment.ownSharesInThisCompany',
                                    ),
                                }}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    formikProps.setTouched({
                                        ...formikProps.touched,
                                        'employment.ownSharesInThisCompany':
                                            true,
                                    });
                                    formikProps.setFieldValue(
                                        'employment.ownSharesInThisCompany',
                                        e.currentTarget.value === 'true',
                                    );
                                }}
                                options={radioButtonOptions}
                            />
                        </Controller>
                        <TextField
                            name='employment.jobTitle'
                            label={`What is ${
                                props.isPrimaryApplicant ? 'your' : 'their'
                            } job title?`}
                            maxLength={255}
                            value={
                                formikProps.values.employment.jobTitle?.toString() ||
                                ''
                            }
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                formikProps.handleChange(e)
                            }
                            onBlur={formikProps.handleBlur}
                            error={getError('employment.jobTitle')}
                        />
                        <Controller
                            label={`What is ${
                                props.isPrimaryApplicant ? 'your' : 'their'
                            } base salary?`}
                            additionalInfo='This should be the annual, pre-tax amount'
                        >
                            <CallOut classes='mb-2'>
                                <p className='text-sm_body mt-0.5'>
                                    We’ll verify your income later
                                </p>
                            </CallOut>

                            <TextField
                                name='incomeAmount'
                                mask={'currency' as any}
                                prefix={<StirlingIcon />}
                                maxLength={255}
                                value={
                                    formikProps.values.incomeAmount?.toString() ||
                                    ''
                                }
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    formikProps.handleChange(e)
                                }
                                onBlur={formikProps.handleBlur}
                                error={getError('incomeAmount')}
                            />
                        </Controller>
                        <Controller
                            label={`Do ${
                                props.isPrimaryApplicant ? 'you' : 'they'
                            } earn any other income?`}
                            additionalInfo='Such as bonus, dividends, pension, second job.'
                        >
                            <IncomingOtherIncomesSubView
                                getError={getError}
                                formikProps={formikProps}
                                getPropsWithError={getPropsWithError}
                                title={'Other incomes'}
                                addButtonTitle={'Add another income source'}
                            />
                        </Controller>
                        <Controller
                            label={`At what age do you think ${
                                props.isPrimaryApplicant ? "you'll" : "they'll"
                            } retire?`}
                            additionalInfo='For most people this will be 66'
                        >
                            <TextField
                                name='estimatedRetirementAge'
                                mask='number'
                                maxLength={2}
                                value={
                                    formikProps.values.estimatedRetirementAge?.toString() ||
                                    ''
                                }
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    formikProps.handleChange(e)
                                }
                                onBlur={formikProps.handleBlur}
                                error={getError('estimatedRetirementAge')}
                            />
                        </Controller>
                        <Controller
                            label={`Do you expect ${
                                props.isPrimaryApplicant ? 'your' : 'their'
                            } income to decrease in the near future?`}
                        >
                            <ExpectedDecrease
                                formikProps={formikProps}
                                getPropsWithError={getPropsWithError}
                                getError={getError}
                            />
                        </Controller>
                        <ErrorContainer formikProps={formikProps} />
                    </div>
                );
            }}
        </Formik>
    );
};

export default IncomingEmployedView;
